import { FlexProps } from "@chakra-ui/react"

/**
 * How the primary view yields space to the panel + toolbar.
 *
 * - `margin` (default): Shift the primary with marginLeft/marginRight (or
 *   `auto` when the layout overflows). Primary width is unchanged.
 * - `constrain`: Shrink and/or translate the primary so it stays within the
 *   remaining layout width (respects `safeInset` / `primaryMaxWidth`). Used
 *   when the primary should compress before the panel overlays it.
 */
export type AugmentationOffsetMode = "margin" | "constrain"

export type AugmentationOffsetOptions =
  | boolean
  | {
      mode?: AugmentationOffsetMode
      safeInset?: number
      primaryMaxWidth?: number
    }

export type ComputeAugmentationOffsetInput = {
  offset?: AugmentationOffsetOptions
  panelPosition: "near" | "far"
  /** Explicit alignment. Left is not fully supported by the panel hook yet. */
  alignAugmentation: "left" | "right"
  panelSize: string
  hasPrimary: boolean
  hasToolbar: boolean
  layoutWidth: number
  primaryWidth: number
  panelWidth: number
  toolbarWidth: number
  primaryAndPanelSpacing: number
  panelAndToolbarSpacing: number
  layoutSpacing: number
}

export type ComputeAugmentationOffsetResult = {
  panelOffset: number
  toolbarOffset: number
  contentInset: { left: number; right: number }
  primaryHorizontalAlignment: FlexProps["justify"]
  primaryOffsetProp: { marginLeft?: string; marginRight?: string }
  primaryMotionProps: { animate?: { x: number } }
  primaryLayout: false | "position"
  primarySizeProps: { w?: string; maxW?: string }
}

type NormalizedOffset = {
  enabled: boolean
  mode: AugmentationOffsetMode
  safeInset: number
  primaryMaxWidth?: number
}

export function normalizeOffset(
  offset?: AugmentationOffsetOptions
): NormalizedOffset {
  if (offset === false) {
    return { enabled: false, mode: "margin", safeInset: 0 }
  }

  if (offset && typeof offset === "object") {
    return {
      enabled: true,
      mode: offset.mode || "margin",
      safeInset: offset.safeInset || 0,
      primaryMaxWidth: offset.primaryMaxWidth
    }
  }

  return { enabled: true, mode: "margin", safeInset: 0 }
}

function getConstrainedPrimary({
  layoutCenter,
  layoutWidth,
  naturalPrimaryWidth,
  augmentationWidth,
  safeInset
}: {
  layoutCenter: number
  layoutWidth: number
  naturalPrimaryWidth: number
  augmentationWidth: number
  safeInset: number
}) {
  const maxPrimaryRight = layoutWidth - augmentationWidth
  const availablePrimaryWidth = maxPrimaryRight - safeInset
  const width = Math.max(
    0,
    Math.min(naturalPrimaryWidth, availablePrimaryWidth)
  )

  const centeredLeft = layoutCenter - width / 2
  const targetLeft = Math.max(
    safeInset,
    Math.min(centeredLeft, maxPrimaryRight - width)
  )

  return {
    width,
    translateX: targetLeft - centeredLeft
  }
}

/**
 * Pure offset policy for Primary / Panel / Toolbar coordination.
 * Modes: `margin` (default) and `constrain`.
 */
export function computeAugmentationOffset({
  offset,
  panelPosition,
  alignAugmentation,
  panelSize,
  hasPrimary,
  hasToolbar,
  layoutWidth,
  primaryWidth,
  panelWidth,
  toolbarWidth,
  primaryAndPanelSpacing,
  panelAndToolbarSpacing,
  layoutSpacing
}: ComputeAugmentationOffsetInput): ComputeAugmentationOffsetResult {
  const config = normalizeOffset(offset)
  const shouldConstrain = config.mode === "constrain"
  const shouldAnimate = shouldConstrain

  // The panel + toolbar width.
  const panelPlusToolbar = panelWidth + panelAndToolbarSpacing + toolbarWidth
  const shouldOffset =
    config.enabled && panelSize !== "full" && panelPlusToolbar > 0
  const naturalPrimaryWidth =
    shouldConstrain && config.primaryMaxWidth
      ? config.primaryMaxWidth
      : shouldConstrain
      ? layoutWidth
      : primaryWidth
  // The primary + panel + toolbar width.
  const primaryPlusPanelPlusToolbar =
    naturalPrimaryWidth + primaryAndPanelSpacing + panelPlusToolbar

  // Offsetting the panel from the side of the layout.
  // This currently assumes the toolbar is positioned adjacent to the panel.
  let panelOffset = panelAndToolbarSpacing + toolbarWidth
  // Default to offset the toolbar from the side of the layout by the layout spacing.
  let toolbarOffset = layoutSpacing
  // Offsetting the primary from the same side of the layout.
  // This is to make room for displaying the panel + toolbar adjacent to the primary
  // when the layout contains all elements (no overflowing elements).
  let primaryOffsetValue = "0px"
  let primaryHorizontalAlignment: FlexProps["justify"] = "center"
  let primaryTranslateX = 0
  let constrainedPrimaryWidth: number | null = null

  // We can use the centers of the layout and primary to derive the position
  // of the left and right edges of the primary. This approach is necessary,
  // rather than getting the primary size left and right values, because
  // these values aren't updated by the resize observer (but widths are).
  // The center of the layout.
  const layoutCenter = layoutWidth / 2
  // Does the layout contain all elements within its width?
  // If so, then we can offset the primary if necessary.
  // If not, then the primary will be "auto" offset from the side of layout.
  const layoutContainsAll =
    !hasPrimary || layoutWidth > primaryPlusPanelPlusToolbar
  // Only ready to account for offsets when all elements are present
  // and widths are known.
  const isReady = Boolean(
    (!hasPrimary || !!primaryWidth) &&
      (!hasToolbar || toolbarWidth) &&
      layoutWidth
  )

  // We can simplify offset logic by adjusting the position of the primary
  // to the right (or left, when implemented), then apply the offset from
  // that side to account for the panel + toolbar. This is simpler than
  // always adjusting the offset for a centered primary view, which would
  // constantly need to adjust as the augmentation layout width changed.
  if (shouldOffset && isReady) {
    // When the layout contains all elements, we can offset the primary.
    //
    // If panel overlaps primary:
    //  - Align the primary view to the side of layout.
    //  - Offset the primary view from that side by the width of the panel + toolbar.
    // If the primary overlaps the layout:
    //  - Align the primary view to the side of layout.
    //  - Offset the primary view from that side by the remaining layout width ("auto" margin).
    // Otherwise:
    //  - Align the primary view to the center of the layout.
    //  - Do not offset the primary view.
    if (layoutContainsAll) {
      // Width of the augmentation (panel + toolbar) when aligned to the side.
      const augmentationWidth = primaryAndPanelSpacing + panelPlusToolbar
      // We can subtract the centered primary edge from the layout width
      // to determine how much space is available for the augmentation.
      const centeredPrimaryEdge = layoutCenter + naturalPrimaryWidth / 2
      const availableLayoutWidth = layoutWidth - centeredPrimaryEdge
      // If there's not enough space for the augmentation, we need to offset
      // the primary view to allow for the augmentation to fit.
      let primaryOffset =
        availableLayoutWidth < augmentationWidth ? augmentationWidth : 0

      if (shouldConstrain && primaryOffset) {
        const constrained = getConstrainedPrimary({
          layoutCenter,
          layoutWidth,
          naturalPrimaryWidth,
          augmentationWidth,
          safeInset: config.safeInset || 0
        })
        constrainedPrimaryWidth = constrained.width
        primaryTranslateX = constrained.translateX
      }

      // When the panel is positioned "near" the primary view, we need to
      // offset the panel and toolbar be adjacent to the side of the primary view.
      // TODO: simplify and fix this logic. Mostly spacing everything correctly, but it
      // would seem we could improve.
      if (panelPosition === "near") {
        // When the primary is being offset, we can adjust all offsets so that all
        // elements are centered in the layout.
        if (primaryOffset) {
          const remainingLayoutWidth = layoutWidth - primaryPlusPanelPlusToolbar
          const nearOffset = remainingLayoutWidth / 2
          panelOffset += nearOffset
          toolbarOffset += nearOffset
          primaryOffset += nearOffset
        } else {
          // When there is no primary offset, we shift the panel and toolbar
          // to the side of the primary view based on the primary's edge.
          const remainingLayoutWidth = layoutWidth - centeredPrimaryEdge
          // Accounting for gutter spacing on the panel (- layoutSpacing).
          // The gutter spacing pushes the panel inward from the edge of the layout.
          panelOffset =
            remainingLayoutWidth - primaryAndPanelSpacing - panelWidth
          toolbarOffset = panelOffset - panelAndToolbarSpacing - toolbarWidth
        }

        // Accounting for gutter spacing on the panel (- layoutSpacing).
        // The gutter spacing pushes the panel inward from the edge of the layout.
        panelOffset -= layoutSpacing
      }

      if (primaryOffset) {
        // When offsetting the primary, we align the primary to the side of the
        // panel + toolbar, and offset the primary from that side (using margin left/right).
        if (!shouldConstrain) {
          primaryHorizontalAlignment =
            alignAugmentation === "left" ? "flex-start" : "flex-end"
          primaryOffsetValue = `${primaryOffset}px`
        }
      }
    } else if (shouldConstrain) {
      // When the layout doesn't contain all elements, we offset and constrain
      // the primary as much as possible while preserving the configured safe inset.
      const augmentationWidth = primaryAndPanelSpacing + panelPlusToolbar
      const maxPrimaryRight = layoutWidth - augmentationWidth
      const availablePrimaryWidth = maxPrimaryRight - (config.safeInset || 0)
      constrainedPrimaryWidth = Math.max(
        0,
        Math.min(naturalPrimaryWidth, availablePrimaryWidth)
      )

      const centeredPrimaryLeft = layoutCenter - constrainedPrimaryWidth / 2
      const targetPrimaryLeft = Math.max(
        config.safeInset || 0,
        maxPrimaryRight - constrainedPrimaryWidth
      )

      primaryTranslateX = targetPrimaryLeft - centeredPrimaryLeft
    } else {
      // When the layout doesn't contain all elements, we simply offset
      // the primary as far from the side of the layout as possible to allow
      // as much of the primary to be visible behind the partially overlaying
      // panel + toolbar.
      primaryOffsetValue = "auto"
    }
  }

  // Depending on alignment of panel, offset the primary from that side.
  const primaryOffsetMarginKey =
    alignAugmentation === "left" ? "marginLeft" : "marginRight"
  // Fixed or portaled content is outside of AugmentationLayoutPrimary and
  // cannot inherit its width/margin styles. Expose the full panel footprint so
  // that content can opt into the same reserved space.
  const contentInsetValue =
    shouldOffset && isReady
      ? primaryAndPanelSpacing + panelWidth + panelOffset + layoutSpacing
      : 0

  return {
    panelOffset,
    toolbarOffset,
    contentInset: {
      left: alignAugmentation === "left" ? contentInsetValue : 0,
      right: alignAugmentation === "right" ? contentInsetValue : 0
    },
    primaryHorizontalAlignment,
    primaryOffsetProp: {
      [primaryOffsetMarginKey]: primaryOffsetValue
    },
    primaryMotionProps: shouldAnimate
      ? {
          animate: { x: primaryTranslateX }
        }
      : {},
    primaryLayout: shouldAnimate ? false : ("position" as const),
    primarySizeProps: {
      w:
        constrainedPrimaryWidth != null
          ? `${constrainedPrimaryWidth}px`
          : shouldConstrain
          ? "100%"
          : undefined,
      maxW:
        shouldConstrain && config.primaryMaxWidth
          ? `${config.primaryMaxWidth}px`
          : undefined
    }
  }
}

type UseAugmentationOffsetProps = ComputeAugmentationOffsetInput

function useAugmentationOffset(props: UseAugmentationOffsetProps) {
  return computeAugmentationOffset(props)
}

export default useAugmentationOffset
